import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as mui from '@mui/material';
import { SideBar } from '../../layout';
import SearchAppBar from '../../common/Appbar';
import Separator from '../../layout/Separator';
import ResponsiveContainer from '../../layout/Container';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import LockIcon from '@mui/icons-material/Lock';
import StorageIcon from '@mui/icons-material/Storage';
import AddIcon from '@mui/icons-material/Add';
import HomeIcon from '@mui/icons-material/Home';
import SFTPService from '../../../services/SFTPService';
import DynamicSnackbar from '../../common/Snackbar';
import CompanyService from '../../../services/CompanyService';

const HRSFTPConfig = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [testLoading, setTestLoading] = useState(false);
    const [companies, setCompanies] = useState([]);
    const [sftpConfig, setSftpConfig] = useState({
        COMPANY_ID: '',
        HOST_NAME: '',
        SFTP_USERNAME: '',
        SFTP_PW_HASHED: '',
        SFTP_SECRET_HASHED: '',
        PORT: '22',
        SFTP_DIRECTORY: '',
        SFTP_DESTINATION: '',
        AUTH_KEY: '',
        SETUP_COMPLETE: false,
        DIRECTORY_VERIFIED: false
    });
    const [testStatus, setTestStatus] = useState({
        tested: false,
        success: false,
        directoryVerified: false
    });
    const [testPassword, setTestPassword] = useState('');
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const [existingConfig, setExistingConfig] = useState(null);

    useEffect(() => {
        // Fetch companies for dropdown
        const fetchCompanies = async () => {
            try {
                const companiesData = await CompanyService.fetchCompanies();
                setCompanies(companiesData);
            } catch (error) {
                console.error('Error fetching companies:', error);
                setSnackbarMessage('Failed to load companies');
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
            }
        };

        // Fetch existing SFTP configurations
        const fetchSFTPConfig = async () => {
            try {
                const configData = await SFTPService.fetchSFTP();
                if (configData && configData.length > 0) {
                    setExistingConfig(configData[0]);
                    setSftpConfig({
                        ...configData[0],
                        SFTP_PW_HASHED: '************', // Mask sensitive data
                        SFTP_SECRET_HASHED: configData[0].SFTP_SECRET_HASHED ? '************' : ''
                    });
                }
            } catch (error) {
                console.error('Error fetching SFTP configuration:', error);
            }
        };

        fetchCompanies();
        fetchSFTPConfig();
    }, []);

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setSftpConfig(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleCompanyChange = (event) => {
        setSftpConfig(prev => ({
            ...prev,
            COMPANY_ID: event.target.value
        }));
    };

    const handleTestPasswordChange = (e) => {
        setTestPassword(e.target.value);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!sftpConfig.HOST_NAME || !sftpConfig.SFTP_USERNAME) {
            setSnackbarMessage('Host name and username are required');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }
        
        setLoading(true);
        
        try {
            let response;
            
            // If there's no password or it's masked, remove it from the update
            const configToSend = { ...sftpConfig };
            if (configToSend.SFTP_PW_HASHED === '************') {
                delete configToSend.SFTP_PW_HASHED;
            }
            if (configToSend.SFTP_SECRET_HASHED === '************') {
                delete configToSend.SFTP_SECRET_HASHED;
            }
            
            if (existingConfig) {
                // Update existing config
                response = await SFTPService.updateSFTP(existingConfig.id, configToSend);
                setSnackbarMessage('SFTP configuration updated successfully');
            } else {
                // Create new config
                response = await SFTPService.createSFTP(configToSend);
                setSnackbarMessage('SFTP configuration created successfully');
                setExistingConfig(response);
            }
            
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
        } catch (error) {
            console.error('Error saving SFTP configuration:', error);
            setSnackbarMessage('Failed to save SFTP configuration');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } finally {
            setLoading(false);
        }
    };

    const handleTestConnection = async () => {
        if (!existingConfig) {
            setSnackbarMessage('Please save the configuration before testing');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }
        
        if (!testPassword) {
            setSnackbarMessage('Password is required for testing');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }
        
        setTestLoading(true);
        setTestStatus({
            tested: true,
            success: false,
            directoryVerified: false
        });
        
        try {
            // Pass optional flag to test the directory as well
            const response = await SFTPService.testSFTPConnection(existingConfig.id, testPassword, true);
            
            if (response.success) {
                let message = 'Connection successful: ' + response.message;
                if (response.directoryExists === true) {
                    message += ' Directory exists and is accessible.';
                } else if (response.directoryExists === false) {
                    message += ' Warning: The specified directory does not exist or is not accessible.';
                }
                
                setSnackbarMessage(message);
                setSnackbarSeverity(response.directoryExists === false ? 'warning' : 'success');
                
                setTestStatus({
                    tested: true,
                    success: true,
                    directoryVerified: response.directoryExists === true
                });
                
                const updatedFields = { 
                    SETUP_COMPLETE: true,
                    DIRECTORY_VERIFIED: response.directoryExists === true
                };
                
                if (!sftpConfig.SETUP_COMPLETE || sftpConfig.DIRECTORY_VERIFIED !== response.directoryExists) {
                    const updatedConfig = { 
                        ...sftpConfig, 
                        ...updatedFields
                    };
                    await SFTPService.updateSFTP(existingConfig.id, updatedFields);
                    setSftpConfig(updatedConfig);
                    setExistingConfig({ ...existingConfig, ...updatedFields });
                }
            } else {
                setSnackbarMessage('Connection failed: ' + response.message);
                setSnackbarSeverity('error');
                
                const updatedFields = { 
                    SETUP_COMPLETE: false,
                    DIRECTORY_VERIFIED: false
                };
                
                if (sftpConfig.SETUP_COMPLETE || sftpConfig.DIRECTORY_VERIFIED) {
                    const updatedConfig = { 
                        ...sftpConfig, 
                        ...updatedFields
                    };
                    await SFTPService.updateSFTP(existingConfig.id, updatedFields);
                    setSftpConfig(updatedConfig);
                    setExistingConfig({ ...existingConfig, ...updatedFields });
                }
            }
        } catch (error) {
            console.error('Error testing SFTP connection:', error);
            setSnackbarMessage('Connection test failed: ' + (error.message || 'Unknown error'));
            setSnackbarSeverity('error');
            
            const updatedFields = { 
                SETUP_COMPLETE: false,
                DIRECTORY_VERIFIED: false
            };
            
            if (sftpConfig.SETUP_COMPLETE || sftpConfig.DIRECTORY_VERIFIED) {
                const updatedConfig = { 
                    ...sftpConfig, 
                    ...updatedFields
                };
                try {
                    await SFTPService.updateSFTP(existingConfig.id, updatedFields);
                    setSftpConfig(updatedConfig);
                    setExistingConfig({ ...existingConfig, ...updatedFields });
                } catch (updateError) {
                    console.error('Error updating config status after failed connection:', updateError);
                }
            }
        } finally {
            setTestLoading(false);
            setSnackbarOpen(true);
        }
    };

    const customMainContent = (
        <div>
            <ResponsiveContainer>
                <mui.Breadcrumbs aria-label="breadcrumb">
                    <mui.Link underline="hover" color="inherit" href="/Dashboard" sx={{ display: 'flex', alignItems: 'center' }}>
                        <HomeIcon sx={{ mr: 0.5, fontSize: '1.1rem' }} />
                        Home
                    </mui.Link>
                    <mui.Link underline="hover" color="inherit" href="/Dashboard">
                        Dashboard
                    </mui.Link>
                    <mui.Link underline="hover" color="inherit" href="/hr-data-management">
                        HR Data
                    </mui.Link>
                    <mui.Typography color="text.primary">SFTP Configuration</mui.Typography>
                </mui.Breadcrumbs>

                <SearchAppBar title="HR SFTP Configuration" icon={<StorageIcon />} />
                <mui.Typography sx={{ marginTop: '20px' }} variant="subtitle2" gutterBottom>
                    Configure connections to HR data on SFTP servers
                </mui.Typography>

                <Separator />

                <mui.Paper elevation={3} sx={{ p: 3, mt: 2, maxWidth: 800, mx: 'auto' }}>
                    <mui.Grid container spacing={3}>
                        <mui.Grid item xs={12}>
                            <mui.Typography variant="h6" gutterBottom>
                                SFTP Server Configuration
                            </mui.Typography>
                            <mui.Typography variant="body2" color="textSecondary" paragraph>
                                Enter the details to connect to your SFTP server where HR data is stored.
                            </mui.Typography>
                        </mui.Grid>
                        
                        <mui.Grid item xs={12} sm={6}>
                            <mui.FormControl fullWidth>
                                <mui.InputLabel id="company-select-label">Company</mui.InputLabel>
                                <mui.Select
                                    labelId="company-select-label"
                                    name="COMPANY_ID"
                                    value={sftpConfig.COMPANY_ID}
                                    label="Company"
                                    onChange={handleCompanyChange}
                                    disabled={!!existingConfig}
                                >
                                    <mui.MenuItem value="">
                                        <em>No specific company</em>
                                    </mui.MenuItem>
                                    {companies.map((company) => (
                                        <mui.MenuItem key={company.id} value={company.id}>
                                            {company.COMPANY_NAME}
                                        </mui.MenuItem>
                                    ))}
                                </mui.Select>
                                <mui.FormHelperText>
                                    Company this SFTP configuration belongs to (optional)
                                </mui.FormHelperText>
                            </mui.FormControl>
                        </mui.Grid>
                        
                        <mui.Grid item xs={12} sm={6}>
                            <mui.TextField
                                fullWidth
                                label="Port"
                                name="PORT"
                                value={sftpConfig.PORT}
                                onChange={handleChange}
                                placeholder="22"
                                type="text"
                            />
                        </mui.Grid>
                        
                        <mui.Grid item xs={12}>
                            <mui.TextField
                                fullWidth
                                label="Host Name"
                                name="HOST_NAME"
                                value={sftpConfig.HOST_NAME}
                                onChange={handleChange}
                                placeholder="sftp.example.com"
                                required
                            />
                        </mui.Grid>
                        
                        <mui.Grid item xs={12} sm={6}>
                            <mui.TextField
                                fullWidth
                                label="Username"
                                name="SFTP_USERNAME"
                                value={sftpConfig.SFTP_USERNAME}
                                onChange={handleChange}
                                placeholder="username"
                                required
                            />
                        </mui.Grid>
                        
                        <mui.Grid item xs={12} sm={6}>
                            <mui.TextField
                                fullWidth
                                label="Password"
                                name="SFTP_PW_HASHED"
                                value={sftpConfig.SFTP_PW_HASHED}
                                onChange={handleChange}
                                type="password"
                                placeholder={existingConfig ? "Leave unchanged to keep current password" : "Enter password"}
                                required={!existingConfig}
                            />
                        </mui.Grid>
                        
                        <mui.Grid item xs={12} sm={6}>
                            <mui.TextField
                                fullWidth
                                label="Source Directory"
                                name="SFTP_DIRECTORY"
                                value={sftpConfig.SFTP_DIRECTORY}
                                onChange={handleChange}
                                placeholder="/path/to/hr/data"
                            />
                            <mui.FormHelperText>
                                Directory on the SFTP server where HR data files are located
                            </mui.FormHelperText>
                        </mui.Grid>
                        
                        <mui.Grid item xs={12} sm={6}>
                            <mui.TextField
                                fullWidth
                                label="Destination Directory"
                                name="SFTP_DESTINATION"
                                value={sftpConfig.SFTP_DESTINATION}
                                onChange={handleChange}
                                placeholder="/path/to/local/destination"
                            />
                            <mui.FormHelperText>
                                Local directory where files should be downloaded
                            </mui.FormHelperText>
                        </mui.Grid>
                        
                        <mui.Grid item xs={12}>
                            <mui.TextField
                                fullWidth
                                label="Auth Key (Optional)"
                                name="AUTH_KEY"
                                value={sftpConfig.AUTH_KEY}
                                onChange={handleChange}
                                multiline
                                rows={3}
                                placeholder="SSH private key if required"
                            />
                        </mui.Grid>
                        
                        <mui.Grid item xs={12}>
                            <mui.Button
                                variant="contained"
                                color="primary"
                                onClick={handleSubmit}
                                disabled={loading}
                                startIcon={loading ? <mui.CircularProgress size={20} /> : null}
                                sx={{ mr: 2 }}
                            >
                                {existingConfig ? 'Update Configuration' : 'Save Configuration'}
                            </mui.Button>
                        </mui.Grid>
                    </mui.Grid>
                </mui.Paper>

                {existingConfig && (
                    <mui.Paper elevation={3} sx={{ p: 3, mt: 3, maxWidth: 800, mx: 'auto' }}>
                        <mui.Typography variant="h6" gutterBottom>
                            Test Connection
                        </mui.Typography>
                        
                        <mui.Typography variant="body2" paragraph>
                            Test your SFTP connection to ensure it's working correctly and verify that the specified directory exists.
                        </mui.Typography>
                        
                        <mui.Grid container spacing={2} alignItems="center">
                            <mui.Grid item xs={12} sm={6}>
                                <mui.TextField
                                    fullWidth
                                    label="Password for Testing"
                                    value={testPassword}
                                    onChange={handleTestPasswordChange}
                                    type="password"
                                    required
                                />
                            </mui.Grid>
                            
                            <mui.Grid item xs={12} sm={6}>
                                <mui.Button
                                    variant="contained"
                                    color="secondary"
                                    onClick={handleTestConnection}
                                    disabled={testLoading}
                                    startIcon={testLoading ? <mui.CircularProgress size={20} color="inherit" /> : <LockIcon />}
                                >
                                    Test Connection
                                </mui.Button>
                            </mui.Grid>
                        </mui.Grid>
                        
                        <mui.Box sx={{ mt: 2 }}>
                            {testStatus.tested ? (
                                testStatus.success ? (
                                    <mui.Alert severity="success">
                                        Connection has been verified and is working properly.
                                        {testStatus.directoryVerified && (
                                            <strong> The specified directory exists.</strong>
                                        )}
                                    </mui.Alert>
                                ) : (
                                    <mui.Alert severity="error">
                                        Connection test failed. Please check your configuration and try again.
                                    </mui.Alert>
                                )
                            ) : existingConfig ? (
                                <mui.Alert severity="info">
                                    Connection needs to be tested. Please enter your password and test the connection.
                                </mui.Alert>
                            ) : null}
                        </mui.Box>
                    </mui.Paper>
                )}
            </ResponsiveContainer>

            <DynamicSnackbar
                open={snackbarOpen}
                message={snackbarMessage}
                severity={snackbarSeverity}
                onClose={handleSnackbarClose}
            />
        </div>
    );

    return (
        <div>
            <SideBar mainContent={customMainContent} />
        </div>
    );
};

export default HRSFTPConfig; 